import { formatEther, getAddress } from "ethers";
import {
  connectWalletRequest,
  connectWalletSuccess,
  connectWalletFailure,
  disconnectWallet as resetWallet,
  updateBalance,
  updateBalanceError,
} from "./wallet-slice";
import {
  getMetaMaskWallet,
  requireAmoy,
  walletRequest,
  walletErrorMessage,
} from "../../services/wallet-provider";

let activeAttempt = null;
let activeInteractive = false;
let generation = 0;
let removeListeners = null;
let pendingRefresh = false;

export const disconnectWallet = () => (dispatch) => {
  generation += 1;
  removeListeners?.();
  removeListeners = null;
  pendingRefresh = false;
  localStorage.removeItem("walletConnected");
  dispatch(resetWallet());
};

function subscribe(wallet, dispatch) {
  removeListeners?.();
  const refresh = () => {
    if (activeAttempt) pendingRefresh = true;
    else dispatch(checkWalletConnection());
  };
  const accountsChanged = (accounts) => {
    if (!accounts.length) dispatch(disconnectWallet());
    else refresh();
  };
  const disconnected = () => dispatch(disconnectWallet());
  wallet.provider.on("accountsChanged", accountsChanged);
  wallet.provider.on("chainChanged", refresh);
  wallet.provider.on("disconnect", disconnected);
  removeListeners = () => {
    wallet.provider.removeListener("accountsChanged", accountsChanged);
    wallet.provider.removeListener("chainChanged", refresh);
    wallet.provider.removeListener("disconnect", disconnected);
  };
}

const runConnection = (interactive) => (dispatch, getState) => {
  if (activeAttempt) {
    // A click during the silent mount check must still be able to authorize/switch.
    if (activeInteractive || !interactive) return activeAttempt;
    const queuedGeneration = generation;
    return activeAttempt.then(() => {
      if (generation === queuedGeneration && !getState().wallet.isConnected)
        return dispatch(connectWallet());
    });
  }
  if (!interactive && localStorage.getItem("walletConnected") !== "true")
    return Promise.resolve();

  const attemptGeneration = generation;
  const isCurrent = () => generation === attemptGeneration;
  dispatch(connectWalletRequest());
  activeInteractive = interactive;
  activeAttempt = (async () => {
    try {
      const wallet = await getMetaMaskWallet();
      if (!isCurrent()) return;
      if (interactive) await walletRequest(wallet, "eth_requestAccounts");
      if (!isCurrent()) return;
      const accounts = await walletRequest(wallet, "eth_accounts");
      if (!isCurrent()) return;
      if (!accounts.length) {
        dispatch(disconnectWallet());
        return;
      }
      subscribe(wallet, dispatch);
      const chainId = await requireAmoy(wallet, interactive);
      if (!isCurrent()) return;
      // Account selection may have changed while the network prompt was open.
      const currentAccounts = await walletRequest(wallet, "eth_accounts");
      if (!isCurrent()) return;
      if (!currentAccounts.length) {
        dispatch(disconnectWallet());
        return;
      }
      const address = getAddress(currentAccounts[0]);
      dispatch(connectWalletSuccess({ address, chainId }));
      localStorage.setItem("walletConnected", "true");
      try {
        const balance = await walletRequest(wallet, "eth_getBalance", [
          address,
          "latest",
        ]);
        if (isCurrent() && !pendingRefresh)
          dispatch(updateBalance(formatEther(balance)));
      } catch (error) {
        if (isCurrent() && !pendingRefresh)
          dispatch(updateBalanceError(walletErrorMessage(error)));
      }
    } catch (error) {
      if (isCurrent())
        dispatch(connectWalletFailure(walletErrorMessage(error)));
    }
  })().finally(() => {
    activeAttempt = null;
    if (pendingRefresh) {
      pendingRefresh = false;
      dispatch(checkWalletConnection());
    }
  });
  return activeAttempt;
};

export const checkWalletConnection = () => runConnection(false);
export const connectWallet = () => runConnection(true);
